// routes
import ModalProvider from "mui-modal-provider";
import { FirestoreProvider } from "contexts/FirestoreContext";
import { WebotServerProvider } from "contexts/WebotServerContext";
import { AlgoliaClientProvider } from "contexts/AlgoliaClientContext";
import { AppProvider } from "contexts/AppContext";
import Router from "./routes";
// import { BrowserRouter } from "react-router-dom";
// theme
import ThemeConfig from "./theme";
// hooks
import useAuth from "./hooks/useAuth";
// components
import RtlLayout from "./components/RtlLayout";
import ScrollToTop from "./components/ScrollToTop";
import LoadingScreen from "./components/LoadingScreen";
import GoogleAnalytics from "./components/GoogleAnalytics";
import NotistackProvider from "./components/NotistackProvider";
import ThemePrimaryColor from "./components/ThemePrimaryColor";
import ThemeLocalization from "./components/ThemeLocalization";

import "customStyles.css";

// slick-carousel
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// lazy image
import "react-lazy-load-image-component/src/effects/blur.css";

import { CrmSpecificationsProvider } from "contexts/CrmSpecificationsContext";
import { ListingDialogProvider } from "contexts/ListingDialogContext";
import { CalendarProvider, CalendarEventEditorHost } from "contexts/CalendarContext";
import { MetaPixel } from "components/analytics/MetaPixel";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";
import { PostHogProvider } from "posthog-js/react";
import { APIProvider } from "@vis.gl/react-google-maps/dist/index.umd";
import Posthog, { posthogKey, posthogOptions } from "components/analytics/Posthog";
import type { APIProvider as APIProviderTypes } from "@vis.gl/react-google-maps";

// ----------------------------------------------------------------------

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const GoogleMapsAPIProvider = APIProvider as typeof APIProviderTypes;

export default function App() {
  const { isInitialized } = useAuth();

  return (
    <ThemeConfig>
      <ThemePrimaryColor>
        <ThemeLocalization>
          <RtlLayout>
            <NotistackProvider>
              <ModalProvider>
                <PostHogProvider apiKey={posthogKey} options={posthogOptions}>
                  <GoogleMapsAPIProvider apiKey={GOOGLE_MAPS_API_KEY} libraries={["places"]}>
                    <FirestoreProvider>
                      <WebotServerProvider>
                        <AlgoliaClientProvider>
                        <AppProvider>
                          <QueryParamProvider
                            adapter={ReactRouter6Adapter}
                            options={{
                              includeAllParams: false,
                              removeDefaultsFromUrl: true,
                              skipUpdateWhenNoChange: true,
                              enableBatching: true
                            }}
                          >
                            <CalendarProvider>
                              <CrmSpecificationsProvider>
                                <ListingDialogProvider>
                                  <ScrollToTop />
                                  <Posthog />
                                  <MetaPixel />
                                  <GoogleAnalytics />
                                  {isInitialized ? <Router /> : <LoadingScreen />}
                                  {/* Inside ListingDialogProvider on purpose — see
                                      CalendarEventEditorContext. */}
                                  <CalendarEventEditorHost />
                                </ListingDialogProvider>
                              </CrmSpecificationsProvider>
                            </CalendarProvider>
                          </QueryParamProvider>
                        </AppProvider>
                        </AlgoliaClientProvider>
                      </WebotServerProvider>
                    </FirestoreProvider>
                  </GoogleMapsAPIProvider>
                </PostHogProvider>
              </ModalProvider>
            </NotistackProvider>
          </RtlLayout>
        </ThemeLocalization>
      </ThemePrimaryColor>
    </ThemeConfig>
  );
}
